import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import {
  BookOpen,
  Plus,
  Sparkles,
  Layers,
  Globe,
  FileText,
  Search,
  RefreshCw,
  ExternalLink,
  AlertCircle,
  Database,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import type { LearnTopic } from '@/client/types/learn';
import {
  useAdminLearnTopicsQuery,
  useDeleteLearnTopicMutation,
  usePublishLearnTopicMutation,
  useReorderLearnTopicsMutation,
  useUnpublishLearnTopicMutation,
} from '@/client/api/learn';
import { SortableTopicCard } from './components/SortableTopicCard';
import { TopicFormDialog } from './components/TopicFormDialog';
import { BootstrapDialog } from './components/BootstrapDialog';

const LearnDashboardPage: React.FC = () => {
  useBreadcrumbs([
    { label: 'Content', href: '/admin/content/pages' },
    { label: 'Learn' },
  ]);

  const { data: topics = [], isLoading, isFetching, refetch } = useAdminLearnTopicsQuery(true);
  const reorderMutation = useReorderLearnTopicsMutation();
  const publishMutation = usePublishLearnTopicMutation();
  const unpublishMutation = useUnpublishLearnTopicMutation();
  const deleteMutation = useDeleteLearnTopicMutation();

  const [searchQuery, setSearchQuery] = useState('');
  const [topicDialogOpen, setTopicDialogOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState<LearnTopic | null>(null);
  const [bootstrapDialogOpen, setBootstrapDialogOpen] = useState(false);
  const [pendingDeleteTopic, setPendingDeleteTopic] = useState<LearnTopic | null>(null);
  const [publishingTopicId, setPublishingTopicId] = useState<string | null>(null);

  // Drag and Drop Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  // Local sorted state for responsive UI during reorder
  const sortedTopics = useMemo(() => {
    return [...topics].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }, [topics]);

  const filteredTopics = useMemo(() => {
    if (!searchQuery.trim()) return sortedTopics;
    const q = searchQuery.toLowerCase().trim();
    return sortedTopics.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        t.id.toLowerCase().includes(q) ||
        t.description?.toLowerCase().includes(q)
    );
  }, [sortedTopics, searchQuery]);

  // Aggregate stats
  const totalTopics = topics.length;
  const publishedTopics = topics.filter((t) => t.published).length;
  const totalSections = topics.reduce((acc, t) => acc + (t.section_count ?? t.sections?.length ?? 0), 0);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sortedTopics.findIndex((t) => t.id === active.id);
    const newIndex = sortedTopics.findIndex((t) => t.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrderList = arrayMove(sortedTopics, oldIndex, newIndex);
    const orderedIds = newOrderList.map((t) => t.id);

    try {
      await reorderMutation.mutateAsync(orderedIds);
      toast.success('Topic order updated');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to reorder topics');
      refetch();
    }
  };

  const handleTogglePublish = async (topic: LearnTopic) => {
    setPublishingTopicId(topic.id);
    try {
      if (topic.published) {
        await unpublishMutation.mutateAsync(topic.id);
        toast.success(`Unpublished "${topic.title}"`, {
          description: 'All sections in this topic are now hidden from public readers.',
        });
      } else {
        await publishMutation.mutateAsync(topic.id);
        toast.success(`Published "${topic.title}"`);
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update topic publish state');
    } finally {
      setPublishingTopicId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteTopic) return;
    const count = pendingDeleteTopic.section_count ?? pendingDeleteTopic.sections?.length ?? 0;
    if (count > 0) {
      toast.error('Cannot delete topic with sections', {
        description: `This topic has ${count} section(s). Move or delete them first before deleting the topic.`,
      });
      setPendingDeleteTopic(null);
      return;
    }

    try {
      await deleteMutation.mutateAsync({ id: pendingDeleteTopic.id });
      toast.success(`Deleted topic "${pendingDeleteTopic.title}"`);
      setPendingDeleteTopic(null);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete topic');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <BookOpen className="h-7 w-7 text-blue-600" />
              <h1 className="text-2xl font-bold text-gray-900">Learn Management</h1>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Manage knowledge base lanes, topics, section ordering, and educational content on FeelyFeely.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBootstrapDialogOpen(true)}
              className="gap-1.5"
            >
              <Sparkles className="h-4 w-4 text-blue-600" />
              Schema & Seed
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setEditingTopic(null);
                setTopicDialogOpen(true);
              }}
              className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="h-4 w-4" />
              New Topic
            </Button>
          </div>
        </div>

        {/* Stats Strip */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Total Topics</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{totalTopics}</p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
              <Layers className="h-5 w-5" />
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Published Topics</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{publishedTopics}</p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
              <Globe className="h-5 w-5" />
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Total Sections</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{totalSections}</p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-50 text-purple-600">
              <FileText className="h-5 w-5" />
            </div>
          </div>
        </div>

        {/* Search & Actions Bar */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search topics by title or ID..."
              className="pl-9"
            />
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              className="gap-1.5 text-gray-600"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="ghost"
              size="sm"
              asChild
              className="gap-1.5 text-blue-600 hover:text-blue-700"
            >
              <a href="/learn" target="_blank" rel="noreferrer">
                View Public /learn
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </Button>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-lg" />
                  <div className="space-y-1.5 flex-1">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
                <Skeleton className="h-10 w-full" />
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-20" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty Catalog State */}
        {!isLoading && topics.length === 0 && (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white p-12 text-center shadow-sm">
            <Database className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-base font-semibold text-gray-900">No Learn Topics Found</h3>
            <p className="mx-auto mt-2 max-w-sm text-sm text-gray-500">
              The Learn catalog appears empty. You can bootstrap the schema and seed the default 8 topics & 20 sections with 1 click.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <Button
                onClick={() => setBootstrapDialogOpen(true)}
                className="gap-2 bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Sparkles className="h-4 w-4" />
                Bootstrap & Seed Catalog
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setEditingTopic(null);
                  setTopicDialogOpen(true);
                }}
              >
                Create Custom Topic
              </Button>
            </div>
          </div>
        )}

        {/* No Search Results State */}
        {!isLoading && topics.length > 0 && filteredTopics.length === 0 && (
          <div className="rounded-xl border border-gray-200 bg-white p-10 text-center shadow-sm">
            <AlertCircle className="mx-auto h-8 w-8 text-gray-400" />
            <p className="mt-2 text-sm font-medium text-gray-900">No topics match &quot;{searchQuery}&quot;</p>
            <Button variant="link" size="sm" onClick={() => setSearchQuery('')} className="mt-1">
              Clear search filter
            </Button>
          </div>
        )}

        {/* Topic Cards Grid with Dnd Reordering */}
        {!isLoading && filteredTopics.length > 0 && (
          <div>
            <div className="mb-2 flex items-center justify-between text-xs text-gray-500 px-1">
              <span>Drag cards by the grab icon to reorder public display lanes</span>
              <span>Showing {filteredTopics.length} of {topics.length} topics</span>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={filteredTopics.map((t) => t.id)}
                strategy={rectSortingStrategy}
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredTopics.map((topic) => (
                    <SortableTopicCard
                      key={topic.id}
                      topic={topic}
                      onEdit={(t) => {
                        setEditingTopic(t);
                        setTopicDialogOpen(true);
                      }}
                      onDelete={(t) => setPendingDeleteTopic(t)}
                      onTogglePublish={handleTogglePublish}
                      isPublishing={publishingTopicId === topic.id}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}

        {/* Topic Form Modal (Create / Edit) */}
        <TopicFormDialog
          open={topicDialogOpen}
          onOpenChange={setTopicDialogOpen}
          topic={editingTopic}
        />

        {/* Bootstrap Dialog */}
        <BootstrapDialog
          open={bootstrapDialogOpen}
          onOpenChange={setBootstrapDialogOpen}
          onCompleted={() => refetch()}
        />

        {/* Delete Confirmation Alert */}
        <AlertDialog
          open={Boolean(pendingDeleteTopic)}
          onOpenChange={(open) => !open && setPendingDeleteTopic(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Topic &quot;{pendingDeleteTopic?.title}&quot;?</AlertDialogTitle>
              <AlertDialogDescription>
                {((pendingDeleteTopic?.section_count ?? pendingDeleteTopic?.sections?.length ?? 0) > 0) ? (
                  <span className="text-amber-700 font-medium block">
                    ⚠️ This topic contains {pendingDeleteTopic?.section_count ?? pendingDeleteTopic?.sections?.length} section(s). You must move or delete its sections first before this topic can be deleted.
                  </span>
                ) : (
                  'This will remove the topic from the catalog. This action cannot be undone.'
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                disabled={(pendingDeleteTopic?.section_count ?? pendingDeleteTopic?.sections?.length ?? 0) > 0}
                className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              >
                Delete Topic
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default LearnDashboardPage;
